import { createSlice } from "@reduxjs/toolkit";

const isGenericOthersLabel = (s) =>
    !s || s === "OTHERS" || s === "Others" || s === "others";

/** Cart row `type` is used in POS tables; Others lines must show custom item name (`activeColor.type` / `name`), not catalog `model`. */
const recalculateCartTotal = (state) => {
    state.total = state.products.reduce(
        (sum, p) => sum + (Number(p.price) || 0) * (Number(p.amount) || 0),
        0
    );
};

const deriveCartLineType = (product) => {
    const ac = product.activeColor || {};
    const ic = ac.itemCode;
    if (ic === "OTHERS" || ic === "others") {
        const trimmedType = ac.type != null ? String(ac.type).trim() : "";
        const trimmedName = ac.name != null ? String(ac.name).trim() : "";
        if (!isGenericOthersLabel(trimmedType)) return trimmedType;
        if (!isGenericOthersLabel(trimmedName)) return trimmedName;
    }
    return product.model;
};

const cartSlice = createSlice({
    name: "ihgroupcart",
    initialState: {
        products: [],
        quantity: 0,
        total: 0,
    },
    reducers: {
        addProduct: (state, action) => {
            const product = action.payload
            const countryPrice = product.activeColor.price ?  product.activeColor.price / action.payload.quantity : 0
            const panelTitle = `${product.activeColor.phase ? `${product.activeColor.phase} Phase - `: ""}${product.activeColor.type ? `${product.activeColor.type} - ` : ""}${product.activeColor.voltage ? `${product.activeColor.voltage}` : ""}`
            const normalTitle = `${product.activeColor.type}`
            const fullTitle = `Inverter :${product.activeColor.type} Inverter ${product.activeColor.KW ? `${product.activeColor.KW}KW` : ""}`

            // For "Others" items (itemCode is OTHERS), use color.name as the title (the custom code)
            const isOthersItem = product.activeColor.itemCode === "OTHERS" || product.activeColor.itemCode === "others";
            const othersTitle = product.activeColor.name && !isGenericOthersLabel(product.activeColor.name) 
                ? product.activeColor.name 
                : (product.activeColor.type && !isGenericOthersLabel(product.activeColor.type) ? product.activeColor.type : "Others");
            
            const activeTitle = isOthersItem 
                ? othersTitle 
                : product.activeColor.name === "Inverter" 
                    ? fullTitle 
                    : product.activeColor.name === "Jinko Panel" 
                        ? panelTitle 
                        : normalTitle
            state.quantity += 1;
            state.products.push({
                _id: product._id,
                title: activeTitle,
                color: product.activeColor,
                itemCode: product.activeColor.pvcGutterItemCode ? product.activeColor.pvcGutterItemCode : product.activeColor.itemCode,
                fabricColor: product.activeColor.fabricColor,
                sidePanelColor: product.activeColor.sidePanelColor,
                fabric: product.activeColor.fabric,
                transparency: product.activeColor.transparency,
                typeDescription: product.activeColor.typeDescription,
                frameColor: product.activeColor.frameColor,
                size: product.activeColor.sizeWidth && product.activeColor.sizeLength ? `${product.activeColor.sizeWidth}' (w) x ${product.activeColor.sizeLength}' (l)` : undefined,
                pvcGutterItemCode: product.activeColor.pvcGutterItemCode,
                pvcGutterType: product.activeColor.pvcGutterType,
                giGutterSize: product.activeColor.giGutterSize,
                aluminumGutterType: product.activeColor.aluminumGutterType,
                aluminumGutterSize: product.activeColor.aluminumGutterSizeWidth && product.activeColor.aluminumGutterSizeLength ? `${product.activeColor.aluminumGutterSizeWidth}' (w) x ${product.activeColor.aluminumGutterSizeLength}' (l)` : undefined,
                flashingItemCode: product.activeColor.flashingItemCode,
                flashingType: product.activeColor.flashingType,
                model: product.model,
                price: countryPrice,
                img: product.img,
                type: deriveCartLineType(product),
                amount: action.payload.quantity,
                record: product.record,
            });
            recalculateCartTotal(state);
        },
        replaceCartProductAtIndex: (state, action) => {
            const { index, ...productPayload } = action.payload;
            const product = productPayload;

       
            const quantity = action.payload.quantity;
            if (
                index < 0 ||
                index >= state.products.length ||
                !Number.isFinite(Number(index))
            ) {
                return;
            }
            const oldProduct = state.products[index];

            const countryPrice = product.activeColor.price
                ? product.activeColor.price / quantity
                : 0;
            const panelTitle = `${product.activeColor.phase ? `${product.activeColor.phase} Phase - ` : ""}${
                product.activeColor.type ? `${product.activeColor.type} - ` : ""
            }${product.activeColor.voltage ? `${product.activeColor.voltage}` : ""}`;
            const normalTitle = `${product.activeColor.type}`;
            const fullTitle = `Inverter :${product.activeColor.type} Inverter ${
                product.activeColor.KW ? `${product.activeColor.KW}KW` : ""
            }`;

            // For "Others" items (itemCode is OTHERS), use color.name as the title (the custom code)
            const isOthersItem = product.activeColor.itemCode === "OTHERS" || product.activeColor.itemCode === "others";
            const othersTitle = product.activeColor.name && !isGenericOthersLabel(product.activeColor.name) 
                ? product.activeColor.name 
                : (product.activeColor.type && !isGenericOthersLabel(product.activeColor.type) ? product.activeColor.type : "Others");
            
            const activeTitle = isOthersItem 
                ? othersTitle 
                : product.activeColor.name === "Inverter"
                    ? fullTitle
                    : product.activeColor.name === "Jinko Panel"
                      ? panelTitle
                      : normalTitle;

            const newLine = {
                _id: product._id,
                title: activeTitle,
                color: product.activeColor,
                itemCode: product.activeColor.pvcGutterItemCode
                    ? product.activeColor.pvcGutterItemCode
                    : product.activeColor.itemCode,
                fabricColor: product.activeColor.fabricColor,
                sidePanelColor: product.activeColor.sidePanelColor,
                fabric: product.activeColor.fabric,
                transparency: product.activeColor.transparency,
                typeDescription: product.activeColor.typeDescription,
                frameColor: product.activeColor.frameColor,
                size:
                    product.activeColor.sizeWidth && product.activeColor.sizeLength
                        ? `${product.activeColor.sizeWidth}' (w) x ${product.activeColor.sizeLength}' (l)`
                        : undefined,
                pvcGutterItemCode: product.activeColor.pvcGutterItemCode,
                pvcGutterType: product.activeColor.pvcGutterType,
                giGutterSize: product.activeColor.giGutterSize,
                aluminumGutterType: product.activeColor.aluminumGutterType,
                aluminumGutterSize:
                    product.activeColor.aluminumGutterSizeWidth &&
                    product.activeColor.aluminumGutterSizeLength
                        ? `${product.activeColor.aluminumGutterSizeWidth}' (w) x ${product.activeColor.aluminumGutterSizeLength}' (l)`
                        : undefined,
                flashingItemCode: product.activeColor.flashingItemCode,
                flashingType: product.activeColor.flashingType,
                model: product.model,
                price: countryPrice,
                img: product.img,
                type: deriveCartLineType(product),
                amount: quantity,
                record: oldProduct.record,
                createdBy: oldProduct.createdBy,
                createdAt: oldProduct.createdAt,
            };
            state.products[index] = newLine;
            recalculateCartTotal(state);
        },
        addQuantity: (state, action) => {
            const product = action.payload
            const countryPrice = product.activeColor.price
            const existingProductIndex = state.color.findIndex(
                (item) => `${item.title}-${item.color.name}` === `${action.payload.title}-${action.payload.activeColor.name}`
            );
            state.color[existingProductIndex].quantity =
            state.color[existingProductIndex].quantity +
                action.payload.quantity;
                
            state.total = state.total + (countryPrice * action.payload.quantity);
            if (state.products[existingProductIndex].quantity <= 0){
                state.products.splice(existingProductIndex, 1);
                state.quantity -= 1
            }
        },
        addQuantityIcon: (state, action) => {
            // Create a unique identifier that includes all distinguishing properties
            const createProductIdentifier = (product) => {
                const color = product.color || product.activeColor;
                return JSON.stringify({
                    name: color.name,
                    itemCode: color.itemCode,
                    type: color.type,
                    frameColor: color.frameColor,
                    sizeWidth: color.sizeWidth,
                    sizeLength: color.sizeLength,
                    sizeHeight: color.sizeHeight,
                    model: product.model,
                    pvcGutterItemCode: color.pvcGutterItemCode,
                    pvcGutterType: color.pvcGutterType,
                    giGutterSize: color.giGutterSize,
                    stainlessSteelGutterSize: color.stainlessSteelGutterSize,
                    aluminumGutterType: color.aluminumGutterType,
                    aluminumGutterSizeWidth: color.aluminumGutterSizeWidth,
                    aluminumGutterSizeLength: color.aluminumGutterSizeLength,
                    flashingItemCode: color.flashingItemCode,
                    flashingType: color.flashingType,
                    steelhollowItemCode: color.steelhollowItemCode,
                    steelhollowType: color.steelhollowType,
                    fabricColor: color.fabricColor,
                    sidePanelColor: color.sidePanelColor,
                    fabric: color.fabric,
                    transparency: color.transparency,
                    ft: color.ft,
                    // Awning fields
                    acpItemCode: color.acpItemCode,
                    acpType: color.acpType,
                    acpFrameColor: color.acpFrameColor,
                    acpSizeWidth: color.acpSizeWidth,
                    acpSizeLength: color.acpSizeLength,
                    glassItemCode: color.glassItemCode,
                    glassType: color.glassType,
                    glassSizeWidth: color.glassSizeWidth,
                    glassSizeLength: color.glassSizeLength,
                    metalDeckItemCode: color.metalDeckItemCode,
                    metalDeckType: color.metalDeckType,
                    metalDeckSizeWidth: color.metalDeckSizeWidth,
                    metalDeckSizeLength: color.metalDeckSizeLength,
                    polycarbonateItemCode: color.polycarbonateItemCode,
                    polycarbonateType: color.polycarbonateType,
                    polycarbonateSizeWidth: color.polycarbonateSizeWidth,
                    polycarbonateSizeLength: color.polycarbonateSizeLength,
                    items: color.items,
                });
            };

            const cartIndex = action.payload.cartIndex;
            let existingProductIndex =
                typeof cartIndex === "number" &&
                cartIndex >= 0 &&
                cartIndex < state.products.length
                    ? cartIndex
                    : state.products.findIndex(
                          (item) =>
                              createProductIdentifier(item) ===
                              createProductIdentifier(action.payload)
                      );

            if (existingProductIndex < 0) {
                return;
            }

            const existingProduct = state.products[existingProductIndex];
            const updatedProduct = {
                ...existingProduct,
                color: { ...existingProduct.color },
            };
            updatedProduct.color.amount =
                (updatedProduct.color.amount || updatedProduct.amount) +
                action.payload.quantity;
            updatedProduct.amount =
                updatedProduct.amount + action.payload.quantity;
            state.products[existingProductIndex] = updatedProduct;

            if (state.products[existingProductIndex].amount <= 0) {
                state.products.splice(existingProductIndex, 1);
                state.quantity = Math.max(0, state.quantity - 1);
            }

            recalculateCartTotal(state);
        },
        editOrderSeedData: (state, action) => {
            const payload = action.payload
           
            let totalquantity = 0
            
            payload.products.forEach((product) => {
                totalquantity += product.amount;
        
                state.products.push({
                    _id: product.productId,
                    title: product.code,
                    color: {
                        name: product.code,
                        price: product.price
                    },
                    price: product.price,
                    img: product.img,
                    amount: product.amount
                });
            });


        

            state.quantity = totalquantity
            state.total = payload.amount
        },
        clearCart: (state) => {
            state.products = [];
            state.quantity = 0;
            state.total = 0;
            state.warranty = [];
            state.order = [];
            state.affiliate = [];
          }
    },
});

export const {
    addProduct,
    replaceCartProductAtIndex,
    addQuantity,
    addQuantityIcon,
    clearCart,
    editOrderSeedData,
} = cartSlice.actions;
export default cartSlice.reducer;
